import type { ApiClient } from './client';
import type { DiscoveryFilters, IssuedBadge, BadgeDefinition, Club, PaginationMeta } from '../../types/discoveryBadges';

export const discoveryBadgeClubMethods = {
  async discoverVehicles(filters: DiscoveryFilters): Promise<{ data: any[]; pagination: PaginationMeta; filters?: any }> {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value));
      }
    });
    const response = await this.makeRequest<any[]>(`/discover_vehicles.php?${queryParams.toString()}`);
    return {
      data: response.data || [],
      pagination: (response as any).pagination || { page: 1, limit: 20, total_count: 0, total_pages: 0 },
      filters: (response as any).filters,
    };
  },

  async getDiscoveryFacets(field: string, q = ''): Promise<{ value: string; count: number }[]> {
    const params = new URLSearchParams({ field, ...(q ? { q } : {}) });
    const response = await this.makeRequest<any[]>(`/discovery_facets.php?${params.toString()}`);
    return response.data || [];
  },

  async getRelatedVehicles(vehicleId: number, limit = 6): Promise<any[]> {
    const response = await this.makeRequest<any[]>(`/related_vehicles.php?vehicle_id=${vehicleId}&limit=${limit}`);
    return response.data || [];
  },

  async getVehicleBadges(vehicleId: number, opts?: { include_revoked?: boolean; category?: string }) {
    const params = new URLSearchParams({ vehicle_id: String(vehicleId) });
    if (opts?.include_revoked) params.set('include_revoked', 'true');
    if (opts?.category) params.set('category', opts.category);
    const response = await this.makeRequest<any>(`/vehicle_badges.php?${params.toString()}`);
    return response.data;
  },

  async getBadgeDetail(publicId: string) {
    const response = await this.makeRequest<any>(`/badge_detail.php?public_id=${encodeURIComponent(publicId)}`);
    return response.data;
  },

  async listBadgeDefinitions(sourceType: string, sourceId: number): Promise<BadgeDefinition[]> {
    const response = await this.makeRequest<BadgeDefinition[]>(
      `/badge_definitions_list.php?source_type=${encodeURIComponent(sourceType)}&source_id=${sourceId}`
    );
    return response.data || [];
  },

  async saveBadgeDefinition(payload: Record<string, unknown>) {
    return this.makeRequest('/badge_definition_save.php', { method: 'POST', body: JSON.stringify(payload) });
  },

  async activateBadgeDefinition(id: number, action: 'activate' | 'archive' = 'activate') {
    return this.makeRequest('/badge_definition_activate.php', {
      method: 'POST',
      body: JSON.stringify({ id, action }),
    });
  },

  async issueBadge(payload: { definition_id: number; vehicle_id: number; note?: string }) {
    return this.makeRequest('/badge_issue.php', { method: 'POST', body: JSON.stringify(payload) });
  },

  async bulkIssueBadges(definitionId: number, vehicleIds: number[]) {
    return this.makeRequest('/badge_bulk_issue.php', {
      method: 'POST',
      body: JSON.stringify({ definition_id: definitionId, vehicle_ids: vehicleIds }),
    });
  },

  async revokeBadge(issuedBadgeId: number, reason?: string) {
    return this.makeRequest('/badge_revoke.php', {
      method: 'POST',
      body: JSON.stringify({ issued_badge_id: issuedBadgeId, reason }),
    });
  },

  async setFeaturedBadges(vehicleId: number, issuedBadgeIds: number[]) {
    return this.makeRequest('/vehicle_featured_badges.php', {
      method: 'POST',
      body: JSON.stringify({ vehicle_id: vehicleId, issued_badge_ids: issuedBadgeIds }),
    });
  },

  async listClubs(filters: Record<string, string | number> = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, String(v));
    });
    const response = await this.makeRequest<Club[]>(`/clubs_list.php?${params.toString()}`);
    return { data: response.data || [], pagination: (response as any).pagination };
  },

  async getClub(slug: string) {
    const response = await this.makeRequest<any>(`/club_get.php?slug=${encodeURIComponent(slug)}`);
    return response.data;
  },

  async saveClub(payload: Record<string, unknown>) {
    return this.makeRequest('/club_save.php', { method: 'POST', body: JSON.stringify(payload) });
  },

  async uploadClubLogo(clubId: number, file: File): Promise<{ logo_path: string }> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const formData = new FormData();
    formData.append('club_id', String(clubId));
    formData.append('logo', file);
    const response = await this.makeRequest<{ logo_path: string }>('/club_logo_upload.php', {
      method: 'POST',
      body: formData,
    });
    if (response.success && response.data?.logo_path) return response.data;
    throw new Error(response.error || response.message || 'Failed to upload club photo');
  },

  async deleteClubLogo(clubId: number): Promise<void> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest('/club_logo_upload.php', {
      method: 'DELETE',
      body: JSON.stringify({ club_id: clubId }),
    });
    if (!response.success) {
      throw new Error(response.error || response.message || 'Failed to remove club photo');
    }
  },

  async submitClub(clubId: number) {
    return this.makeRequest('/club_submit.php', { method: 'POST', body: JSON.stringify({ club_id: clubId }) });
  },

  async requestClubMembership(clubId: number) {
    return this.makeRequest('/club_membership_request.php', {
      method: 'POST',
      body: JSON.stringify({ club_id: clubId }),
    });
  },

  async reviewClubMembership(membershipId: number, decision: 'approve' | 'reject') {
    return this.makeRequest('/club_membership_review.php', {
      method: 'POST',
      body: JSON.stringify({ membership_id: membershipId, decision }),
    });
  },

  async updateClubRole(membershipId: number, role: string) {
    return this.makeRequest('/club_role_update.php', {
      method: 'POST',
      body: JSON.stringify({ membership_id: membershipId, role }),
    });
  },

  async leaveClub(clubId: number) {
    return this.makeRequest('/club_leave.php', { method: 'POST', body: JSON.stringify({ club_id: clubId }) });
  },

  async linkClubVehicle(clubId: number, vehicleId: number, status: 'associated' | 'featured' = 'associated') {
    return this.makeRequest('/club_vehicle_link.php', {
      method: 'POST',
      body: JSON.stringify({ club_id: clubId, vehicle_id: vehicleId, status }),
    });
  },

  async unlinkClubVehicle(clubId: number, vehicleId: number) {
    return this.makeRequest('/club_vehicle_unlink.php', {
      method: 'POST',
      body: JSON.stringify({ club_id: clubId, vehicle_id: vehicleId }),
    });
  },

  async adminListClubs(status = 'pending_review') {
    return this.makeRequest(`/admin/clubs_review.php?status=${encodeURIComponent(status)}`);
  },

  async adminReviewClub(clubId: number, decision: string, notes = '') {
    return this.makeRequest('/admin/clubs_review.php', {
      method: 'POST',
      body: JSON.stringify({ club_id: clubId, decision, notes }),
    });
  },

  async getAdminBadgeModeration() {
    return this.makeRequest<{
      success?: boolean;
      data?: {
        definitions?: Array<Record<string, unknown>>;
        audit?: Array<Record<string, unknown>>;
      };
    }>('/admin/badges_moderation.php');
  },

  async adminBadgeModeration(action: string, payload: Record<string, unknown> = {}) {
    return this.makeRequest('/admin/badges_moderation.php', {
      method: 'POST',
      body: JSON.stringify({ action, ...payload }),
    });
  },

  async getVehicleCertification(vehicleId: number) {
    return this.makeRequest(
      `/vehicle_certification_request.php?vehicle_id=${encodeURIComponent(String(vehicleId))}`
    );
  },

  async submitVehicleCertification(
    vehicleId: number,
    payload: {
      statement: string;
      photo_ids?: number[];
      notes?: string;
      build_sheet_url?: string;
      vin_last4?: string;
      event_registration_ids?: number[];
    }
  ) {
    return this.makeRequest('/vehicle_certification_request.php', {
      method: 'POST',
      body: JSON.stringify({ vehicle_id: vehicleId, action: 'submit', ...payload }),
    });
  },

  async withdrawVehicleCertification(vehicleId: number) {
    return this.makeRequest('/vehicle_certification_request.php', {
      method: 'POST',
      body: JSON.stringify({ vehicle_id: vehicleId, action: 'withdraw' }),
    });
  },

  async adminListVehicleCertifications(status = 'pending_review') {
    return this.makeRequest(
      `/admin/vehicle_certifications.php?status=${encodeURIComponent(status)}`
    );
  },

  async adminReviewVehicleCertification(
    vehicleId: number,
    decision: 'certify' | 'reject' | 'revoke' | 'request_info',
    notes = ''
  ) {
    return this.makeRequest('/admin/vehicle_certifications.php', {
      method: 'POST',
      body: JSON.stringify({ vehicle_id: vehicleId, decision, notes }),
    });
  },
} satisfies ThisType<ApiClient>;

export type { IssuedBadge, BadgeDefinition };
